import { useRef, useState } from 'react';
import { useUserState } from '../../hooks/context/useUserState';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import { useDatasetError } from '../types/types';
import { Dataset } from '../../types/types';
import { requestDataset } from '../requests/dataset/requestDataset';
import { requestDefaultCurrency } from '../requests/requestDefaultCurrency';
import { replaceCurrencyInDataset } from '../../utils/managerUtils';
import { useDefaultNumberFormat } from './useDefaultNumberFormat';
import { needsDefaultNumberFormat } from '../../utils/dataset/needsDefaultNumberFormat';
import { applyDefaultNumberFormat } from '../../utils/dataset/applyDefaultNumberFormat';
import {
  DEFAULT_CURRENCY_FORMAT,
  DEFAULT_CURRENCY_PLACEHOLDER,
  SET_DEFAULT_CURRENCY_FORMAT,
} from '../../constants';

export const useDataset = (id: string) => {
  const {
    dispatch,
    state: { token, defaultCurrencyFormat },
  } = useUserState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<useDatasetError>(undefined);
  const [data, setData] = useState<Dataset | undefined>(undefined);
  const handleApiError = useApiErrorHandling();
  const { resolveDefaultNumberFormat } = useDefaultNumberFormat();
  const latestIdRef = useRef<string | undefined>(undefined);

  const fetch = async (forceHeader?: string) => {
    latestIdRef.current = id;
    let dataset;
    try {
      setIsLoading(true);
      setData(undefined);
      setError(undefined);
      dataset = await requestDataset(id, token, forceHeader);
      // Replace %m with currency formatting string if default currency string was already loaded
      if (defaultCurrencyFormat) {
        dataset = replaceCurrencyInDataset(dataset, defaultCurrencyFormat);
      } else {
        // Check if dataset uses %m, in which case the app needs to fetch default currency
        const usesDefaultCurrency = dataset.items.some((column) =>
          column.format?.includes(DEFAULT_CURRENCY_PLACEHOLDER)
        );
        if (usesDefaultCurrency) {
          let currencyFormat = DEFAULT_CURRENCY_FORMAT;
          try {
            const currencyRes = await requestDefaultCurrency(
              token,
              forceHeader
            );
            if (currencyRes?.format) {
              currencyFormat = currencyRes.format;
              // Save default currency format
              dispatch({
                type: SET_DEFAULT_CURRENCY_FORMAT,
                payload: currencyFormat,
              });
            }
          } catch (error) {
            console.error('Failed to fetch default currency', error);
          } finally {
            // Replace %m in dataset with either fetched currency format or %.2f
            dataset = replaceCurrencyInDataset(dataset, currencyFormat);
          }
        }
      }
      // Float items without any format use the database default number format
      if (needsDefaultNumberFormat(dataset)) {
        const numberFormat = await resolveDefaultNumberFormat(forceHeader);
        if (numberFormat) {
          dataset = applyDefaultNumberFormat(dataset, numberFormat);
        }
      }
      // Fixes issues when switching datasets very fast
      if (latestIdRef.current !== id) return;
      const result: Dataset = { ...dataset, datasetId: id };
      setData(result);
      return result;
    } catch (e) {
      if (latestIdRef.current !== id) return;
      if (e instanceof Error) setError({ error: e, datasetId: id });
      handleApiError(e, true, (forceHeader) => fetch(forceHeader));
    } finally {
      if (latestIdRef.current === id) {
        setIsLoading(false);
      }
    }
  };

  return {
    isLoading,
    error,
    data,
    fetch,
  };
};
